import i18n from '../../i18n';
import { useTerminalExtStore } from '../../store/terminalExtStore';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { useNotificationStore } from '../../store/notificationStore';
import { newPaneId } from '../../store/terminalPaneActions';
import { leafOrder } from './paneTreeModel';
import { presetById } from './terminalLayoutPresets';
import { captureLayout, restoreLayout } from './layoutRestore';
import { getDefaultCwd } from './terminalDefaultCwd';

const notify = (n) => useNotificationStore.getState().addNotification(n);

/**
 * What applying `presetId` to `tabId` would do: the preset tree (existing
 * panes keep their places in reading order; extra leaves get the default
 * shell) and the panes it would CLOSE, so the caller can ask first.
 */
export function planPreset(tabId, presetId) {
  const st = useTerminalExtStore.getState();
  const tab = st.tabs.find((t) => t.id === tabId);
  const preset = presetById(presetId);
  if (!tab || !preset || tab.locked) return null;
  const current = leafOrder(tab.layout);
  const keep = current.slice(0, preset.slots);
  const closing = current.slice(preset.slots).map((id) => st.panes[id]).filter(Boolean);
  const firstCwd = st.panes[current[0]]?.liveCwd || st.panes[current[0]]?.cwd;
  const newPanes = [];
  while (keep.length + newPanes.length < preset.slots) {
    newPanes.push({ id: newPaneId(), kind: 'local', label: 'Terminal', cwd: firstCwd });
  }
  const tree = preset.build([...keep, ...newPanes.map((p) => p.id)]);
  return { tree, newPanes, closing };
}

export function applyPresetPlan(tabId, plan) {
  if (!plan) return;
  useTerminalExtStore.getState().applyTree(tabId, plan.tree, plan.newPanes);
}

/** Open a saved layout in a NEW tab — never touches running panes. */
export function openSavedLayout(layout) {
  const profiles = useTerminalConfigStore.getState().profiles;
  const { tree, specs, notices } = restoreLayout(layout, { profiles, newId: newPaneId, defaultCwd: getDefaultCwd() });
  if (!tree) {
    notify({ type: 'error', title: layout.name, message: i18n.t('terminalExt.layoutCorrupt', 'This layout could not be read.') });
    return null;
  }
  const tabId = useTerminalExtStore.getState().openTreeTab(layout.name, tree, specs);
  const missing = notices.filter((n) => n.kind === 'missingProfile');
  if (missing.length) {
    notify({
      type: 'info',
      title: layout.name,
      message: i18n.t('terminalExt.layoutMissingProfiles', 'Opened with a shell in place of: {{names}} (profile no longer exists).', {
        names: missing.map((n) => n.label).join(', '),
      }),
    });
  }
  return tabId;
}

export function saveTabAsLayout(tabId, { name, rememberCwd, resume }) {
  const st = useTerminalExtStore.getState();
  const tab = st.tabs.find((t) => t.id === tabId);
  if (!tab) return null;
  const layout = captureLayout(tab, st.panes, { name, rememberCwd, resume });
  // No "Layout saved" toast: it sat over the Layouts ▾ button and caught the
  // next click; the layout now listed in that menu is the confirmation.
  useTerminalConfigStore.getState().addLayout(layout);
  return layout;
}

/** Delete with a 5-second Undo in the toast. */
export function deleteLayoutWithUndo(layout) {
  const restore = useTerminalConfigStore.getState().deleteLayout(layout.id);
  notify({
    type: 'info',
    title: i18n.t('terminalExt.layoutDeleted', 'Layout deleted'),
    message: layout.name,
    duration: 5000,
    action: { label: i18n.t('terminalExt.undo', 'Undo'), onClick: restore },
  });
}
